import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { AdPlatformKey, ModificationRequestFeeType, Region } from '@kaizenplatform/hugot-entity';
import { OrganizationId } from '@kaizenplatform/kaizen-id-entity';
import { Chip, Paper, Typography } from '@material-ui/core';
import { findLast, sortBy } from 'lodash';
import { ClassAttributes, createElement, FC, MouseEvent, ReactChild, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import AmazonAdAccount from '../../../entity/AmazonAdAccount';
import DeliverableType from '../../../entity/DeliverableType';
import EditModificationRequestItem from '../../../entity/EditModificationRequestItem';
import FacebookAdAccount from '../../../entity/FacebookAdAccount';
import Language from '../../../entity/Language';
import ModificationRequestEditing from '../../../entity/ModificationRequestEditing';
import PostedModificationRequest from '../../../entity/PostedModificationRequests';
import ProductPricing from '../../../entity/ProductPricing';
import TiktokAdAccount from '../../../entity/TiktokAdAccount';
import UploadingFile from '../../../entity/UploadingFile';
import YahooAdAccount from '../../../entity/YahooAdAccount';
import YoutubeChannel from '../../../entity/YoutubeChannel';
import {
  CreationDetailFragment,
  Deliverable,
  DeliverableForCraetionDetailFragment,
  File,
  ModificationRequestForDetailFragment,
  SingleDirectorUserFragment,
} from '../../../generated/graphql';
import { getLatestDeliverable, getLatestModificationRequest } from '../../../graphql/creation';
import { isDemo } from '../../../graphql/creation';
import { toJSDate } from '../../../graphql/datetime';
import { getLatestDeliverable as getModificationRequestLatestDeliverable } from '../../../graphql/modification_request';
import { useAssignment } from '../../../hooks/offer/useAssignment';
import CreativeSendingAdPlatformSelectorDialog from '../../component/compound/CreativeSendingAdPlatformSelectorDialog';
import CreativeSendingToAmazonDialog from '../../component/compound/CreativeSendingToAmazonDialog';
import CreativeSendingToFacebookDialog from '../../component/compound/CreativeSendingToFacebookDialog';
import CreativeSendingToTiktokDialog from '../../component/compound/CreativeSendingToTiktokDialog';
import CreativeSendingToYahooDialog from '../../component/compound/CreativeSendingToYahooDialog';
import CreativeSendingToYoutubeDialog from '../../component/compound/CreativeSendingToYoutubeDialog';
import DeliverableDetailCard from '../../component/compound/DeliverableDetailCard';
import DummyDeliverableDetailCard from '../../component/compound/DummyDeliverableDetailCard';
import InputCancelDialog from '../../component/compound/ModificationRequestCreatingCard/InputCancelDialog';
import _ModificationRequestDeliverables from '../../component/compound/ModificationRequestDeliverables';
import Select, { SelectItem } from '../../component/compound/Select';
import Spinner from '../../component/compound/Spinner';
import PublicId from '../../component/primitive/PublicId';
import { SCREEN_LARGE } from '../../constant/pageWidth';
import { SPACING_HUGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import mq from '../../styleUtility/mediaquery';
import { default as _CompleteForm } from './CompleteForm';
import CreationCreativeFormat from './CreationCreativeFormat';
import _ModificationRequestCard from './ModificationRequestCard';

type Props = ClassAttributes<HTMLElement> & {
  loading: boolean;
  creation: CreationDetailFragment | null;
  region: Region;
  profileUrl: string;
  uploadedAdPlatformKeys: Map<string, Array<string>>;
  postedModificationRequests: PostedModificationRequest;
  modificationRequestEditing: ModificationRequestEditing;
  onClickRequestStart: () => Promise<void>;
  onPushInputtingItem: (
    modificationRequestId: string,
    bucketId: string | null,
    inputDuration: string,
    inputComment: string,
    files: Array<FileReference>,
  ) => Promise<void>;
  onSelectDeliverable: (deliverableId: string) => void;
  onSelectFeeType: (feeType: ModificationRequestFeeType) => void;
  onUploadModificationRequestAssetFiles: (files: UploadingFile[]) => void;
  onCancelModificationrequestEditing: () => Promise<void>;
  onUploadIssueAssetFiles: (files: UploadingFile[], bucketId: string, itemId: string) => void;
  onUploadIssueCommentAssetFiles: (files: UploadingFile[], deliverableId: string, issueId: string) => void;
  onRemoveModificationRequestItem: (modificationRequestItemId: string, index: number) => void;
  onRemoveAssetFile: (bucketId: string, fileId: string) => void;
  onRemoveAllAssetFile: () => void;
  onRemoveAssetIssueFile: (bucketId: string, fileId: string, issueId: string) => void;
  onRemoveAssetIssueCommentFile: (bucketId: string, fileId: string, issueId: string) => void;
  onRemoveEditAssetFile: (fileId: string) => void;
  onOpenEditModificationRequestDialog: (index: number) => void;
  onCloseEditModificationRequestDialog: () => void;
  onSubmitEditModificationRequestDialog: (
    index: number,
    editModificationRequestItem: EditModificationRequestItem,
    modificationRequestItemId: string,
    duration: string,
    comment: string,
  ) => void;
  onRequestEditAssetFiles: (files: UploadingFile[], bucketId: string) => void;
  onRequestSendModificationRequest: () => Promise<void>;
  onSubmitModificationRequestIssue: (
    modificationRequestItemId: string,
    inputComment: string,
    bucketId: string | null,
    files: Array<FileReference>,
  ) => Promise<void>;
  onSubmitModificationRequestIssueComment: (issueId: string, comment: string) => void;
  onRequestResolveModificationRequestIssue: (id: string) => void;
  onRequestReopenModificationRequestIssue: (id: string) => void;
  onRefetchCreation: () => void;
  fetchFacebookAdAccounts: () => Promise<boolean | void>;
  fetchAmazonAdAccounts: () => Promise<boolean | void>;
  fetchAmazonUploadedCreatives: (creativeId: string) => Promise<boolean | void>;
  isUploadedToAmazon: boolean;
  fetchYahooAdAccounts: () => Promise<boolean | void>;
  fetchYahooUploadedCreatives: (creativeId: string) => Promise<boolean | void>;
  isUploadedToYahoo: boolean;
  fetchTiktokAdAccounts: () => Promise<boolean | void>;
  fetchTiktokUploadedCreatives: (creativeId: string) => Promise<boolean | void>;
  isUploadedToTiktok: boolean;
  onClickAnyDeliverableDownloadButton: (
    e: MouseEvent<HTMLElement>,
    deliverable: {
      creative: {
        file: Pick<File, 'id' | 'url' | 'mimeType'>;
      };
      attachedThumbnail?: {
        file: Pick<File, 'id' | 'url' | 'mimeType'>;
      } | null;
    },
    type: DeliverableType,
  ) => void;
  onRequestSendingCreativeToFacebook: (
    e: MouseEvent<HTMLElement>,
    deliverable: DeliverableForCraetionDetailFragment,
    facebookAdAccount: FacebookAdAccount,
  ) => void;
  onRequestSendingCreativeToYoutube: (
    e: MouseEvent<HTMLElement>,
    deliverable: DeliverableForCraetionDetailFragment,
    youtubeChannel: YoutubeChannel,
  ) => void;
  onRequestSendingCreativeToAmazon: (
    e: MouseEvent<HTMLElement>,
    deliverable: DeliverableForCraetionDetailFragment,
    amazonAdAccount: AmazonAdAccount,
  ) => void;
  onRequestSendingCreativeToYahoo: (
    e: MouseEvent<HTMLElement>,
    deliverable: DeliverableForCraetionDetailFragment,
    yahooAdAccount: YahooAdAccount,
  ) => void;
  onRequestSendingCreativeToTiktok: (
    e: MouseEvent<HTMLElement>,
    deliverable: DeliverableForCraetionDetailFragment,
    tiktokAdAccount: TiktokAdAccount,
  ) => void;
  onRequestAddYoutubeChannel: (e: MouseEvent<HTMLElement>) => void;
  onApproveModificationRequest: (modificationRequestId: string) => void;
  onMount: () => void;
  onUnmount: () => void;
  className?: string;
  organizationId: OrganizationId;
  pointBalance: number | null;
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  language: Language;
  email: string;
  onPurchasedPoint: (point: number) => Promise<void>;
  prepareUser: () => Promise<SingleDirectorUserFragment | void>;
};

const ShowCreationPage: FC<Props> = ({
  creation,
  region,
  loading,
  uploadedAdPlatformKeys,
  postedModificationRequests,
  modificationRequestEditing,
  onClickRequestStart,
  onPushInputtingItem,
  onSelectDeliverable,
  onSelectFeeType,
  onUploadModificationRequestAssetFiles,
  onCancelModificationrequestEditing,
  onUploadIssueAssetFiles,
  onUploadIssueCommentAssetFiles,
  onRemoveModificationRequestItem,
  onRemoveAssetFile,
  onRemoveAllAssetFile,
  onRemoveAssetIssueFile,
  onRemoveAssetIssueCommentFile,
  onRemoveEditAssetFile,
  onOpenEditModificationRequestDialog,
  onCloseEditModificationRequestDialog,
  onSubmitEditModificationRequestDialog,
  onRequestEditAssetFiles,
  onRequestSendModificationRequest,
  onSubmitModificationRequestIssue,
  onSubmitModificationRequestIssueComment,
  onRequestResolveModificationRequestIssue,
  onRequestReopenModificationRequestIssue,
  onRefetchCreation,
  onClickAnyDeliverableDownloadButton,
  fetchFacebookAdAccounts,
  fetchAmazonAdAccounts,
  fetchAmazonUploadedCreatives,
  isUploadedToAmazon,
  fetchYahooAdAccounts,
  fetchYahooUploadedCreatives,
  isUploadedToYahoo,
  fetchTiktokAdAccounts,
  fetchTiktokUploadedCreatives,
  isUploadedToTiktok,
  onRequestSendingCreativeToFacebook,
  onRequestSendingCreativeToYoutube,
  onRequestSendingCreativeToAmazon,
  onRequestSendingCreativeToYahoo,
  onRequestSendingCreativeToTiktok,
  onRequestAddYoutubeChannel,
  onApproveModificationRequest,
  className,
  organizationId,
  pointBalance,
  isPointRequired,
  productPricing,
  profileUrl,
  onMount,
  onUnmount,
  language,
  email,
  onPurchasedPoint,
  prepareUser,
}) => {
  const { loadAssignment, dataAssignment, loadingAssignment } = useAssignment();
  useEffect(() => {
    if (creation?.assignmentId && !isDemo({ id: creation.id })) {
      loadAssignment(creation?.assignmentId);
    }
  }, [creation?.assignmentId, creation?.id, loadAssignment]);

  const assignment = dataAssignment?.assignment;

  const [selectedSendingAdPlatform, setSelectedSendingAdPlatform] = useState<AdPlatformKey | null>(null);
  const [isSendingDialogOpen, setSendingDialogOpen] = useState(false);
  const [isCancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBeforeDeliverableId, setSelectedBeforeDeliverableId] = useState('');
  const [isEditable, setEditable] = useState(false);

  type DeliverableSelectType = 'deliverable' | 'modificationRequestDeliverable';
  const DeliverableSelectType: Record<DeliverableSelectType, DeliverableSelectType> = {
    deliverable: 'deliverable',
    modificationRequestDeliverable: 'modificationRequestDeliverable',
  };

  const latestModificationRequestDeliverable = <D extends { createdAt: any }>(modificationRequest: {
    deliverables: Array<D>;
  }): D | undefined => {
    if (!modificationRequest.deliverables) {
      return;
    }
    return sortBy(modificationRequest.deliverables, (a) => -toJSDate(a.createdAt).valueOf())[0];
  };

  const creationLatestDeliverable = creation && getLatestDeliverable(creation);
  const latestModificationRequest = creation && getLatestModificationRequest(creation);
  const modificationRequestLatestDeliverable =
    latestModificationRequest && getModificationRequestLatestDeliverable(latestModificationRequest);
  const latestDeliverable = modificationRequestLatestDeliverable
    ? modificationRequestLatestDeliverable
    : creation
    ? creationLatestDeliverable
    : null;

  const selectedId = postedModificationRequests.selectedId;

  const selectedCreationDeliverable = creation
    ? selectedId
      ? creation.deliverables.find((deliverable) => deliverable.id === selectedId)
      : latestModificationRequest
      ? creation.deliverables.find((deliverable) => deliverable.id === latestModificationRequest.targetDeliverableId)
      : undefined
    : undefined;

  let selectedDeliverable:
    | (Pick<Deliverable, 'id' | 'intent' | 'createdAt'> & {
        creative: {
          file: Pick<File, 'id' | 'mimeType' | 'url'>;
        };
        attachedThumbnail?: {
          file: Pick<File, 'id' | 'mimeType' | 'size' | 'url'>;
        } | null;
      })
    | undefined = selectedCreationDeliverable;
  let selectedType = DeliverableSelectType.deliverable;

  if (!selectedDeliverable && postedModificationRequests.modificationRequests) {
    const modificationRequest = postedModificationRequests.modificationRequests.find((modificationRequest) => {
      const deliverable = getModificationRequestLatestDeliverable(modificationRequest);
      return deliverable ? deliverable.id === selectedId : false;
    });
    selectedDeliverable = modificationRequest
      ? getModificationRequestLatestDeliverable(modificationRequest)
      : undefined;
    if (selectedDeliverable) {
      selectedType = DeliverableSelectType.modificationRequestDeliverable;
    }
  }
  const selectedDeliverableId = selectedDeliverable ? selectedDeliverable.id : undefined;

  let selectedModificationRequest: ModificationRequestForDetailFragment | undefined = undefined;
  if (selectedDeliverableId) {
    if (selectedType === DeliverableSelectType.deliverable) {
      selectedModificationRequest = postedModificationRequests.modificationRequests
        ? findLast(
            postedModificationRequests.modificationRequests,
            (modificationRequest) => modificationRequest.targetDeliverableId === selectedDeliverableId,
          )
        : undefined;
    } else {
      selectedModificationRequest = postedModificationRequests.modificationRequests
        ? postedModificationRequests.modificationRequests.find((modificationRequest) => {
            const deliverable = latestModificationRequestDeliverable(modificationRequest);
            return modificationRequest.state !== 'approved' && deliverable
              ? deliverable.id === selectedDeliverableId
              : false;
          })
        : undefined;
    }
  }

  useEffect(() => {
    onMount();
    return () => onUnmount();
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (latestDeliverable) {
      onSelectDeliverable(latestDeliverable.id);
      setEditable(false);
    }
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creation]);

  const latestModificationRequestLatestDeliverable =
    latestModificationRequest && getModificationRequestLatestDeliverable(latestModificationRequest);
  const modificationRequestDeliverableItem =
    latestModificationRequest &&
    latestModificationRequest.state == 'responded' &&
    latestModificationRequestLatestDeliverable ? (
      <SelectItem value={latestModificationRequestLatestDeliverable.id} key={'modificationRequestDeliverable'}>
        <VersionItem>
          <DeliverableId>
            <PublicId id={latestModificationRequestLatestDeliverable.id} color="textSecondary" />
          </DeliverableId>
        </VersionItem>
      </SelectItem>
    ) : null;

  const selectItems: ReactChild[] = creation
    ? [
        ...sortBy(creation.deliverables, (a) => -toJSDate(a.createdAt).valueOf()).map(
          (deliverable, index, deliverables) => {
            const version_number = deliverables.length - index;
            return (
              <SelectItem value={deliverable.id} key={`deliverable-${index}`}>
                <VersionItem>
                  <DeliverableId>
                    <PublicId id={deliverable.id} color="textSecondary" />
                  </DeliverableId>
                  <DeliverableVersion
                    label={
                      <FormattedMessage
                        id="modification_request.version_select.version_number"
                        values={{ version_number }}
                      />
                    }
                    variant="outlined"
                    size="small"
                  />
                </VersionItem>
              </SelectItem>
            );
          },
        ),
      ]
    : [<SelectItem key={'deliverable'} />];

  if (modificationRequestDeliverableItem) {
    selectItems.unshift(modificationRequestDeliverableItem);
  }

  const versionSelect =
    creation && creation.deliverables ? (
      <VersionSelect>
        <Select
          label={<FormattedMessage id="modification_request.version_select.label" />}
          defaultValue={latestDeliverable ? latestDeliverable.id : ''}
          overrideValue={selectedId}
          onChange={(e) => {
            if (e.target.value !== selectedDeliverableId) {
              // 入力中の修正依頼はバージョン切替時にキャンセルしておく
              if (modificationRequestEditing.createModificationRequest.items.length > 0) {
                setSelectedBeforeDeliverableId(String(e.target.value));
                setCancelDialogOpen(true);
              } else {
                onCancelModificationrequestEditing();
                setEditable(false);
                onSelectDeliverable(String(e.target.value));
              }
            }
          }}
        >
          {selectItems}
        </Select>
      </VersionSelect>
    ) : null;

  const acceptMessage = !loading ? (
    selectedModificationRequest && selectedModificationRequest.state == 'responded' ? (
      <LatestDeliverableTouchedMessage>
        <FormattedMessage id="show_creation_page.latest_deliverable_touched" />
      </LatestDeliverableTouchedMessage>
    ) : null
  ) : null;

  const Deliverable =
    !loading && !loadingAssignment && creation && creationLatestDeliverable && selectedDeliverable ? (
      <DeliverableDetailCard
        assignment={assignment ?? null}
        creation={creation}
        deliverable={selectedDeliverable}
        selectedItemCanDownloadOrUpload={selectedType === DeliverableSelectType.deliverable}
        modificationRequestApprovable={selectedType === DeliverableSelectType.modificationRequestDeliverable}
        onClickApproveModificationRequestButton={() => onApproveModificationRequest(selectedModificationRequest!.id)}
        onClickDownloadButton={(e: any, type: DeliverableType) => {
          onClickAnyDeliverableDownloadButton(e, selectedDeliverable!, type);
        }}
        onClickSendButton={() => {
          const deliverable = selectedCreationDeliverable ? selectedCreationDeliverable : creationLatestDeliverable;
          onSelectDeliverable(deliverable.id);
          fetchAmazonUploadedCreatives(deliverable.creative.id);
          fetchYahooUploadedCreatives(deliverable.creative.id);
          fetchTiktokUploadedCreatives(deliverable.creative.id);
          setSendingDialogOpen(true);
        }}
        profileUrl={profileUrl}
      />
    ) : (
      <DummyDeliverableDetailCard />
    );

  // 修正依頼のヘッダのセクション
  // - 修正依頼の有無でヘッダのメッセージを変化させます。
  let ModificationRequestHeading = null;
  const showModificationRequestSection =
    creation &&
    !isDemo(creation) &&
    !creation.finalized &&
    creationLatestDeliverable &&
    assignment?.status == 'ASSIGNED';

  if (showModificationRequestSection) {
    let formattedMessage = null;
    if (selectedModificationRequest == null) {
      formattedMessage = <FormattedMessage id="show_creation_page.modification_request_heading_unsubmitted" />;
    } else {
      formattedMessage = <FormattedMessage id="show_creation_page.modification_request_heading_submitted" />;
    }

    ModificationRequestHeading = loading ? null : (
      <ModificationRequestHeadings id="#modificationRequest">
        <Typography variant="h5" color="textSecondary" id="modificationRequest">
          {formattedMessage}
        </Typography>
      </ModificationRequestHeadings>
    );
  }

  const Modification = loading ? (
    <ModificationRequestInLoading />
  ) : showModificationRequestSection ? (
    <ModificationRequestCard
      organizationId={organizationId}
      pointBalance={pointBalance}
      creation={creation}
      isSelectedLatestDeliverable={
        selectedCreationDeliverable ? selectedCreationDeliverable.id === creationLatestDeliverable.id : false
      }
      isEditable={isEditable}
      setEditable={setEditable}
      createModificationRequest={modificationRequestEditing.createModificationRequest}
      editModificationRequestItem={modificationRequestEditing.editModificationRequestItem}
      modificationRequestEditing={modificationRequestEditing}
      assets={postedModificationRequests.assets}
      modificationRequest={selectedModificationRequest}
      isPointRequired={isPointRequired}
      productPricing={productPricing}
      language={language}
      email={email}
      onPurchasedPoint={onPurchasedPoint}
      prepareUser={prepareUser}
      onClickRequestStart={onClickRequestStart}
      onSelectFeeType={onSelectFeeType}
      onPushInputtingItem={onPushInputtingItem}
      onUploadModificationRequestAssetFiles={onUploadModificationRequestAssetFiles}
      onCancelModificationrequestEditing={onCancelModificationrequestEditing}
      onUploadIssueCommentAssetFiles={onUploadIssueCommentAssetFiles}
      onUploadIssueAssetFiles={onUploadIssueAssetFiles}
      onRemoveModificationRequestItem={onRemoveModificationRequestItem}
      onRemoveAssetFile={onRemoveAssetFile}
      onRemoveAllAssetFile={onRemoveAllAssetFile}
      onRemoveAssetIssueFile={onRemoveAssetIssueFile}
      onRemoveAssetIssueCommentFile={onRemoveAssetIssueCommentFile}
      onRemoveEditAssetFile={onRemoveEditAssetFile}
      onOpenEditModificationRequestDialog={onOpenEditModificationRequestDialog}
      onCloseEditModificationRequestDialog={onCloseEditModificationRequestDialog}
      onSubmitEditModificationRequestDialog={onSubmitEditModificationRequestDialog}
      onRequestEditAssetFiles={onRequestEditAssetFiles}
      onRequestSendModificationRequest={onRequestSendModificationRequest}
      onSubmitModificationRequestIssue={onSubmitModificationRequestIssue}
      onRequestResolveModificationRequestIssue={onRequestResolveModificationRequestIssue}
      onRequestReopenModificationRequestIssue={onRequestReopenModificationRequestIssue}
      onSubmitModificationRequestIssueComment={onSubmitModificationRequestIssueComment}
      onRefetchCreation={onRefetchCreation}
    />
  ) : null;

  let ModificationRequestDeliverablesHeading = null;

  if (selectedModificationRequest && selectedModificationRequest.deliverables.length > 0) {
    ModificationRequestDeliverablesHeading = loading ? null : (
      <ModificationRequestDeliverablesHeadings id="#modificationRequestDeliverables">
        <Typography variant="h5" color="textSecondary" id="modificationRequestDeliverables">
          <FormattedMessage id="show_creation_page.modification_request_deliverables_heading" />
        </Typography>
      </ModificationRequestDeliverablesHeadings>
    );
  }

  const Deliverables =
    selectedModificationRequest && selectedModificationRequest.deliverables.length > 0 ? (
      <ModificationRequestDeliverables
        modificationRequest={selectedModificationRequest}
        deliverables={selectedModificationRequest.deliverables}
      />
    ) : null;

  const cancelCreativeSending = () => {
    setSelectedSendingAdPlatform(null);
    setSendingDialogOpen(false);
  };

  const dialogs =
    creation &&
    creation.deliverables.map((deliverable) => {
      return [
        <CreativeSendingAdPlatformSelectorDialog
          creative={deliverable.creative}
          region={region}
          uploadedAdPlatforms={uploadedAdPlatformKeys.get(deliverable.id)}
          open={isSendingDialogOpen && selectedSendingAdPlatform == null}
          onRequestSelectingNetwork={async (_, adPlatform) => {
            if (adPlatform === 'facebook') {
              const isReady = await fetchFacebookAdAccounts();
              if (isReady) {
                setSelectedSendingAdPlatform(adPlatform);
                return;
              }
            } else if (adPlatform === 'google') {
              setSelectedSendingAdPlatform(adPlatform);
              return;
            } else if (adPlatform === 'amazon') {
              const isReady = await fetchAmazonAdAccounts();
              if (isReady) {
                setSelectedSendingAdPlatform(adPlatform);
                return;
              }
            } else if (adPlatform === 'yahoo') {
              const isReady = await fetchYahooAdAccounts();
              if (isReady) {
                setSelectedSendingAdPlatform(adPlatform);
                return;
              }
            } else if (adPlatform === 'tiktok') {
              const isReady = await fetchTiktokAdAccounts();
              if (isReady) {
                setSelectedSendingAdPlatform(adPlatform);
                return;
              }
            }
            cancelCreativeSending();
          }}
          onRequestClose={() => cancelCreativeSending()}
          isUploadedToAmazon={isUploadedToAmazon}
          isUploadedToYahoo={isUploadedToYahoo}
          isUploadedToTiktok={isUploadedToTiktok}
          key={`networkSelector_${deliverable.id}`}
        />,
        <CreativeSendingToFacebookDialog
          open={postedModificationRequests.selectedId === deliverable.id && selectedSendingAdPlatform === 'facebook'}
          onRequestSendingCreative={(e, selected) => onRequestSendingCreativeToFacebook(e, deliverable, selected)}
          onRequestClose={() => cancelCreativeSending()}
          key={`sendingToFacebook_${deliverable.id}`}
        />,
        <CreativeSendingToYoutubeDialog
          open={postedModificationRequests.selectedId === deliverable.id && selectedSendingAdPlatform === 'google'}
          onRequestSendingCreative={(e, selected) => onRequestSendingCreativeToYoutube(e, deliverable, selected)}
          onRequestAddYoutubeChannel={onRequestAddYoutubeChannel}
          onRequestClose={() => cancelCreativeSending()}
          key={`sendingToYoutube_${deliverable.id}`}
        />,
        <CreativeSendingToAmazonDialog
          open={postedModificationRequests.selectedId === deliverable.id && selectedSendingAdPlatform === 'amazon'}
          onRequestSendingCreative={(e, selected) => onRequestSendingCreativeToAmazon(e, deliverable, selected)}
          onRequestClose={() => cancelCreativeSending()}
          key={`sendingToAmazon_${deliverable.id}`}
        />,
        <CreativeSendingToYahooDialog
          open={postedModificationRequests.selectedId === deliverable.id && selectedSendingAdPlatform === 'yahoo'}
          onRequestSendingCreative={(e, selected) => onRequestSendingCreativeToYahoo(e, deliverable, selected)}
          onRequestClose={() => cancelCreativeSending()}
          key={`sendingToYahoo_${deliverable.id}`}
        />,
        <CreativeSendingToTiktokDialog
          open={postedModificationRequests.selectedId === deliverable.id && selectedSendingAdPlatform === 'tiktok'}
          onRequestSendingCreative={(e, selected) => onRequestSendingCreativeToTiktok(e, deliverable, selected)}
          onRequestClose={() => cancelCreativeSending()}
          key={`sendingToTiktok_${deliverable.id}`}
        />,
      ];
    });

  const cancelDialog = (
    <InputCancelDialog
      isCancelDialogOpen={isCancelDialogOpen}
      setCancelDialog={setCancelDialogOpen}
      onClickCancelButton={() => {
        onCancelModificationrequestEditing();
        setEditable(false);
        onSelectDeliverable(selectedBeforeDeliverableId);
      }}
    />
  );

  return (
    <Root className={className}>
      <Heading>
        <CreativeFormat>
          <CreationCreativeFormat creation={creation} />
        </CreativeFormat>
      </Heading>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {versionSelect}
        {creation ? (
          isDemo(creation) ? null : (
            <CompleteForm creation={creation} reload={() => onRefetchCreation()} />
          )
        ) : null}
      </div>

      <LatestDeliverableWithReputation>
        {acceptMessage}
        {Deliverable}
      </LatestDeliverableWithReputation>

      {ModificationRequestHeading}

      {Modification}

      {ModificationRequestDeliverablesHeading}

      {Deliverables}

      {dialogs}

      {cancelDialog}
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['1fr'],
    rows: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
    areas: [
      ['heading'],
      ['versionSelect'],
      ['latestDeliverableWithReputation'],
      ['reputations'],
      ['modificationRequestHeading'],
      ['modificationRequestCard'],
      ['modificationRequestDeliverablesHeading'],
      ['modificationRequestDeliverables'],
    ],
  }),
  {
    width: 'auto',
    maxWidth: SCREEN_LARGE,
    marginTop: SPACING_HUGE,
    marginRight: 'auto',
    paddingBottom: SPACING_UNIT * 12,
    marginLeft: 'auto',
    [mq('sp')]: {
      paddingLeft: SPACING_REGULAR,
      paddingRight: SPACING_REGULAR,
    },
  },
);

const Heading = styled('div')(
  gridLayoutItem('heading'),
  gridLayoutContainer({
    columns: ['1fr'],
    rows: ['auto', 'auto'],
    areas: [['title'], ['creativeFormat']],
  }),
  {
    marginBottom: SPACING_UNIT * 5,
    [mq('sp')]: {
      marginBottom: SPACING_UNIT * 3,
    },
  },
);

const CreativeFormat = styled('div')(gridLayoutItem('creativeFormat'));

const VersionSelect = styled('div')(gridLayoutItem('versionSelect'), {
  marginBottom: SPACING_REGULAR,
});

const VersionItem = styled('div')(flexibleLayoutContainer({ alignItems: 'center' }));

const DeliverableId = styled('span')({
  marginLeft: SPACING_UNIT,
});

const DeliverableVersion = styled(Chip)({
  fontSize: 11,
  marginLeft: SPACING_UNIT,
});

const LatestDeliverableWithReputation = styled(Paper)(gridLayoutItem('latestDeliverableWithReputation'), {
  marginBottom: SPACING_UNIT * 5,
});

const LatestDeliverableTouchedMessage = styled('div')({
  marginTop: SPACING_REGULAR,
  padding: SPACING_REGULAR,
});

const ModificationRequestHeadings = styled('div')(gridLayoutItem('modificationRequestHeading'), {
  marginBottom: SPACING_REGULAR,
});

const ModificationRequestInLoading = styled(Spinner)(gridLayoutItem('modificationRequestCard'), {
  marginBottom: SPACING_UNIT * 5,
});

const ModificationRequestCard = styled(_ModificationRequestCard)(gridLayoutItem('modificationRequestCard'), {
  marginBottom: SPACING_UNIT * 5,
});

const ModificationRequestDeliverables = styled(_ModificationRequestDeliverables)(
  gridLayoutItem('modificationRequestDeliverables'),
  {
    marginBottom: SPACING_UNIT * 5,
  },
);

const ModificationRequestDeliverablesHeadings = styled('div')(
  gridLayoutItem('modificationRequestDeliverablesHeading'),
  {
    marginBottom: SPACING_REGULAR,
  },
);

const CompleteForm = (styled(_CompleteForm)({
  padding: SPACING_UNIT,
}) as unknown) as typeof _CompleteForm;

export default ShowCreationPage;
